import { useCallback, useEffect, useRef, useState, type MouseEvent } from "react";
import { Link, useNavigate, useParams } from "react-router-dom";
import { createHostBridge, type HostBridge } from "@wlh/learning-bridge";
import { BRIDGE_VERSION, type GuestMessage, type LearningItemId } from "@wlh/shared-types";
import { openLearningItem, postBridgeEvent, type OpenItemResponse } from "../api";
import { Shell } from "../ui";

type State =
  | { phase: "loading" }
  | { phase: "error"; message: string }
  | { phase: "external"; url: string }
  | { phase: "playing"; open: Extract<OpenItemResponse, { kind: "HTML_PACKAGE" }> };

// Anne, 2026-09-11: the "Mark this section complete" button should only be
// clickable once the learner has genuinely spent time with the material, not
// the instant the page opens. Lowered 5→3 min, 2026-09-12: real Respect@Work
// sections run as short as 3-4 minutes, so 5 was longer than some content.
const MIN_ENGAGEMENT_SECONDS = 3 * 60;
const MIN_ENGAGEMENT_MINUTES = MIN_ENGAGEMENT_SECONDS / 60;

function formatRemaining(elapsedSeconds: number): string {
  const remaining = Math.max(0, MIN_ENGAGEMENT_SECONDS - elapsedSeconds);
  const m = Math.floor(remaining / 60);
  const s = remaining % 60;
  return `${m}:${s.toString().padStart(2, "0")}`;
}

export function PlayerView() {
  const { itemId } = useParams<{ itemId: string }>();
  const navigate = useNavigate();
  const [state, setState] = useState<State>({ phase: "loading" });
  const [progress, setProgress] = useState<number | null>(null);
  const [completed, setCompleted] = useState(false);
  const [marking, setMarking] = useState(false);
  const [markError, setMarkError] = useState<string | null>(null);
  const [elapsedSeconds, setElapsedSeconds] = useState(0);
  const frameRef = useRef<HTMLIFrameElement>(null);
  const bridgeRef = useRef<HostBridge | null>(null);
  const openedAtRef = useRef<number | null>(null);

  useEffect(() => {
    if (!itemId) return;
    openLearningItem(itemId as LearningItemId)
      .then((open) => {
        if (open.kind === "EXTERNAL_LINK") {
          setState(
            open.url
              ? { phase: "external", url: open.url }
              : { phase: "error", message: "No link configured" },
          );
        } else {
          setState({ phase: "playing", open });
        }
      })
      .catch((e: unknown) =>
        setState({ phase: "error", message: e instanceof Error ? e.message : String(e) }),
      );
  }, [itemId]);

  useEffect(() => {
    if (state.phase !== "playing") return;
    if (openedAtRef.current === null) openedAtRef.current = Date.now();
    const tick = () => {
      const openedAt = openedAtRef.current;
      if (openedAt !== null) setElapsedSeconds(Math.floor((Date.now() - openedAt) / 1000));
    };
    tick();
    const interval = setInterval(tick, 1000);
    return () => clearInterval(interval);
  }, [state.phase]);

  const onGuestEvent = useCallback((message: GuestMessage) => {
    if (message.type === "PROGRESS_UPDATED") setProgress(Math.round(message.percent));
    postBridgeEvent(message)
      .then((res) => {
        if (message.type === "MODULE_COMPLETED") {
          if (res.accepted) setCompleted(true);
          else setMarkError(res.reason ?? "The LMS could not record completion.");
          bridgeRef.current?.send(
            res.accepted
              ? {
                  type: "COMPLETION_ACCEPTED",
                  bridgeVersion: "1.0",
                  sessionToken: message.sessionToken,
                  eventId: message.eventId,
                }
              : {
                  type: "COMPLETION_REJECTED",
                  bridgeVersion: "1.0",
                  sessionToken: message.sessionToken,
                  eventId: message.eventId,
                  reason: "UNKNOWN",
                },
          );
        }
      })
      .catch((e: unknown) => {
        if (message.type === "MODULE_COMPLETED") {
          setMarkError(e instanceof Error ? e.message : "Could not reach the LMS.");
        }
      });
  }, []);

  const onFrameLoad = useCallback(() => {
    if (state.phase !== "playing" || !frameRef.current) return;
    bridgeRef.current?.dispose();
    const bridge = createHostBridge({
      packagesOrigin: state.open.packagesOrigin,
      frame: frameRef.current,
      learningItemId: (itemId ?? "") as LearningItemId,
      sessionToken: state.open.sessionToken,
      resumeState: state.open.resumeState,
      onGuestEvent,
    });
    bridgeRef.current = bridge;
    bridge.init();
  }, [state, itemId, onGuestEvent]);

  useEffect(() => () => bridgeRef.current?.dispose(), []);

  // Most WithAWill content is a single static HTML file with no bridge
  // JavaScript in it, so it can never call bridge.reportCompleted() itself.
  // This button lets the LEARNER (not the package) trigger completion — the
  // API still runs the exact same session/enrolment/item validation as a
  // package-reported completion. Opening the page never completes it; only
  // this deliberate click, or a package that self-reports, does.
  const markComplete = useCallback(() => {
    if (state.phase !== "playing" || completed || marking) return;
    setMarking(true);
    setMarkError(null);
    const eventId =
      typeof crypto !== "undefined" && "randomUUID" in crypto
        ? crypto.randomUUID()
        : `${Date.now()}-${Math.random()}`;
    postBridgeEvent({
      type: "MODULE_COMPLETED",
      bridgeVersion: BRIDGE_VERSION,
      sessionToken: state.open.sessionToken,
      eventId,
    })
      .then((res) => {
        if (res.accepted) setCompleted(true);
        else setMarkError(res.reason ?? "The LMS could not record completion.");
      })
      .catch((e: unknown) =>
        setMarkError(e instanceof Error ? e.message : "Could not reach the LMS."),
      )
      .finally(() => setMarking(false));
  }, [state, completed, marking]);

  if (state.phase === "loading") return <Shell title="Loading…">{null}</Shell>;
  if (state.phase === "error")
    return (
      <Shell title="Cannot open">
        <p>{state.message}</p>
        <Link to="/">← My Learning</Link>
      </Shell>
    );
  if (state.phase === "external")
    return (
      <Shell title="External resource">
        <p>
          <a href={state.url} target="_blank" rel="noreferrer">
            Open the resource ↗
          </a>
        </p>
      </Shell>
    );

  const backTo = `/learn/tracks/${state.open.trackId}`;
  const minTimeElapsed = elapsedSeconds >= MIN_ENGAGEMENT_SECONDS;

  function handleBackClick(e: MouseEvent) {
    if (completed || minTimeElapsed) return;
    e.preventDefault();
    const ok = window.confirm(
      `You've spent less than ${MIN_ENGAGEMENT_MINUTES} minutes on this material. Are you sure you want to leave before completing the minimum recommended time?`,
    );
    if (ok) navigate(backTo);
  }

  return (
    <div style={{ display: "flex", flexDirection: "column", height: "100vh" }}>
      <div
        style={{
          padding: "0.5rem 1rem",
          borderBottom: "1px solid #e6e6ee",
          display: "flex",
          justifyContent: "space-between",
          alignItems: "center",
          gap: "0.75rem",
          fontFamily: "system-ui, sans-serif",
        }}
      >
        <Link to={backTo} onClick={handleBackClick}>
          ← Back to track
        </Link>
        <div style={{ display: "flex", alignItems: "center", gap: "0.75rem" }}>
          {markError && <span style={{ fontSize: "0.85rem", color: "#b00020" }}>{markError}</span>}
          <span style={{ fontSize: "0.9rem", color: completed ? "#0a7d33" : "#666" }}>
            {completed ? "Completed ✓" : progress !== null ? `${progress}%` : "In progress"}
          </span>
          {!completed && (
            <button
              type="button"
              onClick={markComplete}
              disabled={marking || !minTimeElapsed}
              title={
                minTimeElapsed
                  ? undefined
                  : `Spend at least ${MIN_ENGAGEMENT_MINUTES} minutes with this material before marking it complete`
              }
              style={{
                padding: "0.35rem 0.8rem",
                borderRadius: 7,
                border: "none",
                background: marking || !minTimeElapsed ? "#9a9ab0" : "#1a1a2e",
                color: "#fff",
                fontSize: "0.85rem",
                cursor: marking || !minTimeElapsed ? "default" : "pointer",
              }}
            >
              {marking
                ? "Marking…"
                : minTimeElapsed
                  ? "Mark this section complete"
                  : `Available in ${formatRemaining(elapsedSeconds)}`}
            </button>
          )}
        </div>
      </div>
      <iframe
        ref={frameRef}
        onLoad={onFrameLoad}
        src={state.open.packageUrl}
        title="Learning content"
        sandbox="allow-scripts"
        referrerPolicy="no-referrer"
        style={{ border: "none", flex: 1, width: "100%" }}
      />
    </div>
  );
}
